/** @jsxImportSource @emotion/react */
import CampaignStatusBadge from "./CampaignStatusBadge";
import ReservationCountdown from "./ReservationCountdown";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import t from "../../../theme";
import {
  CAMPAIGN_STATUS,
  formatDateTime,
  getAvailabilityCount,
  getAvailabilityText,
  getCampaignDescription,
  getCampaignEndDate,
  getCampaignImage,
  getCampaignStatus,
  getCampaignTitle,
  getCampaignUuid,
  getReservationExpiry,
} from "../utils";

const styles = {
  card: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minHeight: 210,
    border: `1px solid ${t.green.border}`,
    borderRadius: 16,
    padding: 18,
    background: t.glass.primary,
    backdropFilter: t.blur.medium,
    WebkitBackdropFilter: t.blur.medium,
    boxShadow: "0 12px 30px rgba(46, 125, 50, 0.08)",
    fontFamily: t.font.family,
    cursor: "pointer",
    transition:
      "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
    "&:hover": {
      borderColor: "#00AEEF",
      boxShadow: "0 16px 34px rgba(15, 23, 42, 0.13)",
    },
    "&:focus-visible": {
      outline: "3px solid rgba(0, 174, 239, 0.28)",
      outlineOffset: 3,
      borderColor: "#00AEEF",
    },
  },
  compactCard: {
    position: "relative",
    height: "auto",
    minHeight: 0,
    aspectRatio: "3 / 2",
    borderRadius: 20,
    padding: 0,
    border: 0,
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.18)",
    overflow: "hidden",
  },
  bookedCard: {
    background: "rgba(236, 253, 243, 0.72)",
    backdropFilter: t.blur.medium,
    WebkitBackdropFilter: t.blur.medium,
    borderColor: "rgba(84, 172, 52, 0.34)",
    "&:hover": {
      borderColor: "#54ac34",
      boxShadow: "0 16px 34px rgba(84, 172, 52, 0.16)",
    },
  },
  image: {
    width: "100%",
    height: 120,
    borderRadius: "16px 16px 10px 10px",
    objectFit: "cover",
    background:
      "linear-gradient(135deg, rgba(0, 174, 239, 0.16), rgba(0, 128, 96, 0.20))",
    border: "1px solid #edf2f7",
  },
  compactImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    borderRadius: 0,
    border: 0,
  },
  imageWrap: {
    position: "relative",
    width: "calc(100% + 36px)",
    margin: "-18px -18px 0",
  },
  compactImageWrap: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    margin: 0,
  },
  imageFallback: {
    width: "100%",
    height: 120,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    color: "#008060",
    background:
      "linear-gradient(135deg, rgba(0, 174, 239, 0.16), rgba(0, 128, 96, 0.20))",
    border: "1px solid #edf2f7",
    "& svg": {
      fontSize: 28,
      color: "#008060",
    },
    "& span": {
      color: "#475467",
      fontSize: "0.72rem",
      fontWeight: 800,
      lineHeight: 1,
    },
  },
  compactImageFallback: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    borderRadius: 0,
    border: 0,
    "& svg": {
      fontSize: 42,
    },
  },
  imageStatus: {
    position: "absolute",
    top: 8,
    right: 8,
    backdropFilter: "blur(8px)",
    "& span": {
      background: "rgba(255, 255, 255, 0.78)",
      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.10)",
      fontSize: "0.54rem",
      padding: "4px 7px",
      gap: 3,
      "&::before": {
        width: 5,
        height: 5,
      },
    },
  },
  title: {
    margin: 0,
    color: "#101828",
    fontSize: "0.9rem",
    lineHeight: 1.25,
    fontWeight: 700,
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  desc: {
    margin: 0,
    color: "#667085",
    fontSize: "0.78rem",
    lineHeight: 1.5,
  },
  compactDesc: {
    fontSize: 12,
    lineHeight: 1.35,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  meta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    color: "#475467",
    fontSize: "0.72rem",
    fontWeight: 600,
  },
  compactMeta: {
    width: "100%",
    justifyContent: "center",
    gap: 8,
    fontSize: "0.68rem",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    marginTop: "auto",
    textAlign: "center",
    "& span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  compactTopOverlay: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 2,
  },
  compactBottomOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "34px 12px 12px",
    textAlign: "left",
    background:
      "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 100%)",
  },
  compactOverlayTitle: {
    margin: 0,
    color: "#ffffff",
    fontSize: "0.86rem",
    fontWeight: 800,
    lineHeight: 1.25,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
  },
  compactOverlayRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  compactOverlayAvailability: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    color: "rgba(255, 255, 255, 0.88)",
    fontSize: "0.8rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.45)",
    "& svg": {
      fontSize: 19,
      flexShrink: 0,
    },
  },
  compactOverlayButton: {
    flexShrink: 0,
    border: "1px solid rgba(255, 255, 255, 0.4)",
    borderRadius: 999,
    background: "#54ac34",
    color: "#ffffff",
    cursor: "pointer",
    fontFamily: t.font.family,
    fontSize: "0.62rem",
    fontWeight: 800,
    lineHeight: 1,
    padding: "7px 12px",
    whiteSpace: "nowrap",
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.35)",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#4a9b2e",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": {
      outline: "2px solid rgba(255, 255, 255, 0.6)",
      outlineOffset: 2,
    },
  },
  compactOverlayButtonReserved: {
    background: "#c2410c",
    "&:hover": {
      background: "#a8380a",
    },
  },
  compactOverlayButtonBooked: {
    background: "#175cd3",
    "&:hover": {
      background: "#134fb8",
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: "auto",
  },
  compactFooter: {
    gap: 8,
  },
  button: {
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  compactButton: {
    minHeight: 30,
    padding: "4px 11px",
    fontSize: 12,
    boxShadow: "none",
    "& .MuiButton-endIcon": {
      marginLeft: 4,
      "& svg": {
        fontSize: 16,
      },
    },
  },
  compactTimer: {
    borderRadius: 999,
    padding: "5px 8px",
    background: "#fff7ed",
    color: "#9a3412",
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "@media (max-width: 600px)": {
    footer: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
};

const COMPACT_ACTION_BY_STATUS = {
  [CAMPAIGN_STATUS.REDEEMED]: {
    label: "View Ticket",
    variant: styles.compactOverlayButtonBooked,
  },
  [CAMPAIGN_STATUS.RESERVED]: {
    label: "Pay Now",
    variant: styles.compactOverlayButtonReserved,
  },
};
const DEFAULT_COMPACT_ACTION = { label: "Book Ticket", variant: null };

const CampaignCard = ({ campaign, onOpen, compact = false }) => {
  const uuid = getCampaignUuid(campaign);
  const title = getCampaignTitle(campaign);
  const status = getCampaignStatus(campaign);
  const image = getCampaignImage(campaign);
  const expiresAt = getReservationExpiry(campaign);
  const endDate = getCampaignEndDate(campaign);
  const availabilityText = getAvailabilityText(campaign);
  const availabilityCount = getAvailabilityCount(campaign);
  const showTimer = status === CAMPAIGN_STATUS.RESERVED && expiresAt;
  const { label: compactActionLabel, variant: compactButtonVariant } =
    COMPACT_ACTION_BY_STATUS[status] || DEFAULT_COMPACT_ACTION;
  const openCampaign = () => {
    if (uuid) onOpen(uuid);
  };
  const onCardKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openCampaign();
    }
  };
  const onBookButtonClick = (event) => {
    event.stopPropagation();
    openCampaign();
  };

  return (
    <article
      css={[
        styles.card,
        compact && styles.compactCard,
        status === CAMPAIGN_STATUS.REDEEMED && styles.bookedCard,
      ]}
      role="button"
      tabIndex={uuid ? 0 : -1}
      onClick={openCampaign}
      onKeyDown={onCardKeyDown}
      aria-label={`Open ${title}`}
    >
      <div css={[styles.imageWrap, compact && styles.compactImageWrap]}>
        {image ? (
          <img css={[styles.image, compact && styles.compactImage]} src={image} alt="" />
        ) : (
          <div
            css={[
              styles.imageFallback,
              compact && styles.compactImageFallback,
            ]}
          >
            <CampaignOutlinedIcon />
            {!compact && <span>Campaign</span>}
          </div>
        )}
        {!compact && (
          <div css={styles.imageStatus}>
            <CampaignStatusBadge status={status} />
          </div>
        )}
        {compact && (
          <div css={styles.compactTopOverlay}>
            <CampaignStatusBadge status={status} size="small" />
          </div>
        )}
        {compact && (
          <div css={styles.compactBottomOverlay}>
            <h3 css={styles.compactOverlayTitle} title={title}>
              {title}
            </h3>
            <div css={styles.compactOverlayRow}>
              <span css={styles.compactOverlayAvailability}>
                <ConfirmationNumberOutlinedIcon />
                {availabilityCount}
              </span>
              <button
                type="button"
                css={[styles.compactOverlayButton, compactButtonVariant]}
                onClick={onBookButtonClick}
              >
                {compactActionLabel}
              </button>
            </div>
          </div>
        )}
      </div>
      {!compact && (
        <h3 css={styles.title} title={title}>
          {title}
        </h3>
      )}
      {!compact && <p css={styles.desc}>{getCampaignDescription(campaign)}</p>}
      {!compact && (
        <div css={styles.meta}>
          <span>{availabilityText}</span>
          {endDate && <span>Ends {formatDateTime(endDate)}</span>}
        </div>
      )}
      {!compact && (
        <div css={styles.footer}>
          {showTimer ? <ReservationCountdown expiresAt={expiresAt} /> : <span />}
        </div>
      )}
    </article>
  );
};

export default CampaignCard;
